import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Search, Upload, Download, Edit2, Trash2, 
  ChevronLeft, ChevronRight, Filter, X, FileSpreadsheet, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AdminLayout } from './AdminDashboard';
import { employeeAPI } from '../lib/api';

const EmployeeMaster = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    name: '',
    email: '',
    mobile: '',
    department: '',
    designation: '',
    location: ''
  });

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (departmentFilter) params.department = departmentFilter;
      
      const [empResponse, countResponse] = await Promise.all([
        employeeAPI.getAll(params),
        employeeAPI.getCount(params)
      ]);
      
      setEmployees(empResponse.data);
      setTotalCount(countResponse.data.count);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [page, search, departmentFilter]);

  const loadDepartments = async () => {
    try {
      const response = await employeeAPI.getDepartments();
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Failed to load departments');
    }
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      loadEmployees();
    }, 300);
    return () => clearTimeout(debounce);
  }, [loadEmployees]);

  const resetForm = () => {
    setFormData({
      employee_id: '',
      name: '',
      email: '',
      mobile: '',
      department: '',
      designation: '',
      location: ''
    });
    setEditingEmployee(null);
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleEdit = (employee) => {
    setFormData({
      employee_id: employee.employee_id,
      name: employee.name,
      email: employee.email,
      mobile: employee.mobile,
      department: employee.department,
      designation: employee.designation,
      location: employee.location
    });
    setEditingEmployee(employee);
    setShowAddDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEmployee) {
        await employeeAPI.update(editingEmployee.employee_id, formData);
        toast.success('Employee updated successfully');
      } else {
        await employeeAPI.create(formData);
        toast.success('Employee added successfully');
      }
      setShowAddDialog(false);
      resetForm();
      loadEmployees();
      loadDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    
    try {
      await employeeAPI.delete(employeeId);
      toast.success('Employee deleted successfully');
      loadEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/template`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_upload_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx)');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const token = sessionStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/upload-excel`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Upload failed');
      }

      setUploadResult(result);
      toast.success(result.message);
      loadEmployees();
      loadDepartments();
    } catch (error) {
      toast.error(error.message || 'Failed to upload file');
      setUploadResult({ success: false, error: error.message });
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const totalPages = Math.ceil(totalCount / 10);

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">Employee Master</h1>
            <p className="text-muted-foreground">Manage employee data for survey access</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => setShowUploadDialog(true)}
              data-testid="upload-excel-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Excel
            </Button>
            <Button 
              onClick={handleOpenAddDialog}
              data-testid="add-employee-btn"
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Employee
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, ID, or email..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  data-testid="search-input"
                  className="pl-10"
                />
              </div>
              <Select 
                value={departmentFilter || '__all__'} 
                onValueChange={(value) => { setDepartmentFilter(value === '__all__' ? '' : value); setPage(1); }}
              >
                <SelectTrigger className="w-full md:w-48" data-testid="department-filter">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Departments</SelectItem>
                  {departments.filter(d => d).map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(search || departmentFilter) && (
                <Button 
                  variant="ghost" 
                  onClick={() => { setSearch(''); setDepartmentFilter(''); setPage(1); }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Employee Table */}
        <Card data-testid="employee-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                      </TableCell>
                    </TableRow>
                  ) : employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No employees found
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((employee) => (
                      <TableRow key={employee.employee_id} data-testid={`employee-row-${employee.employee_id}`}>
                        <TableCell className="font-mono font-medium">{employee.employee_id}</TableCell>
                        <TableCell>{employee.name}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                            {employee.department}
                          </span>
                        </TableCell>
                        <TableCell>{employee.designation}</TableCell>
                        <TableCell className="font-mono text-sm">{employee.mobile}</TableCell>
                        <TableCell>{employee.location}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(employee)}
                              data-testid={`edit-${employee.employee_id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(employee.employee_id)}
                              data-testid={`delete-${employee.employee_id}`}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, totalCount)} of {totalCount}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">Page {page} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </DialogTitle>
              <DialogDescription>
                {editingEmployee ? 'Update employee details' : 'Add a new employee to the system'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employee_id">Employee ID</Label>
                  <Input
                    id="employee_id"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="Enter Employee ID"
                    disabled={!!editingEmployee}
                    data-testid="employee-id-field"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter full name"
                    data-testid="name-field"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@gnosis.com"
                  data-testid="email-field"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number</Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="Enter mobile number"
                  data-testid="mobile-field"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Select 
                    value={formData.department || undefined} 
                    onValueChange={(value) => setFormData({ ...formData, department: value })}
                  >
                    <SelectTrigger data-testid="department-field">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.filter(d => d).map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="designation">Designation</Label>
                  <Input
                    id="designation"
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    placeholder="Manager"
                    data-testid="designation-field"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Mumbai"
                  data-testid="location-field"
                  required
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-employee-btn">
                  {editingEmployee ? 'Update' : 'Add Employee'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Excel Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                Upload Employee Master
              </DialogTitle>
              <DialogDescription>
                Upload an Excel file to bulk import or update employees
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Step 1: Download Template */}
              <div className="p-4 bg-muted rounded-xl">
                <h4 className="font-medium mb-2">Step 1: Download Template</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Download the Excel template with correct column headers
                </p>
                <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>

              {/* Step 2: Upload File */}
              <div className="p-4 border-2 border-dashed border-border rounded-xl">
                <h4 className="font-medium mb-2">Step 2: Upload Filled Excel</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Fill in employee data and upload the file (.xlsx format)
                </p>
                <div className="flex items-center gap-3">
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    data-testid="file-upload-input"
                    className="flex-1"
                  />
                </div>
                {uploading && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    Processing file...
                  </div>
                )}
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className={`p-4 rounded-xl ${uploadResult.success ? 'bg-accent/10' : 'bg-destructive/10'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {uploadResult.success ? (
                      <CheckCircle className="h-5 w-5 text-accent" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-destructive" />
                    )}
                    <h4 className="font-medium">
                      {uploadResult.success ? 'Upload Complete' : 'Upload Failed'}
                    </h4>
                  </div>
                  
                  {uploadResult.success ? (
                    <div className="space-y-1 text-sm">
                      <p className="text-accent">✓ {uploadResult.created} employees created</p>
                      <p className="text-chart-4">↻ {uploadResult.updated} employees updated</p>
                      {uploadResult.errors?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-destructive">✗ {uploadResult.errors.length} errors:</p>
                          <div className="max-h-32 overflow-y-auto mt-1 text-xs bg-background p-2 rounded">
                            {uploadResult.errors.slice(0, 10).map((err, i) => (
                              <p key={i} className="text-destructive">
                                Row {err.row}: {err.employee_id} - {err.error}
                              </p>
                            ))}
                            {uploadResult.errors.length > 10 && (
                              <p className="text-muted-foreground">...and {uploadResult.errors.length - 10} more</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-destructive">{uploadResult.error}</p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowUploadDialog(false); setUploadResult(null); }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default EmployeeMaster;
